import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import Monogram from './layout/Monogram';

const Profile: React.FC = () => {
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  const [primaryEmail, setPrimaryEmail] = useState<string>('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);

  useEffect(() => {
    const loadPrimaryEmail = async () => {
      try {
        const response = await apiService.getProfile();
        if (response.success && response.user) {
          setPrimaryEmail(response.user.primaryEmail || '');
        }
      } catch (error) {
        console.warn('Failed to load primary email:', error);
      }
    };
    loadPrimaryEmail();
  }, []);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  if (!user) return null;

  const savePrimaryEmail = async () => {
    try {
      setEmailSaving(true);
      const response = await apiService.updateProfile({ primaryEmail });
      if (response.success) {
        setEmailSaved(true);
        setTimeout(() => setEmailSaved(false), 4000);
      } else {
        alert('Failed to update primary email. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save primary email:', error);
      alert('Failed to update primary email. Please try again.');
    } finally {
      setEmailSaving(false);
    }
  };

  return (
    <div className="section">
      <div className="wrap" style={{ maxWidth: 640 }}>
        <span className="eyebrow" style={{ display: 'block', marginBottom: 8 }}>
          Account
        </span>
        <h1 style={{ fontSize: 30, marginBottom: 24 }}>Account settings</h1>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            <Monogram name={user.name} size={48} />
            <div>
              <div style={{ fontWeight: 500 }}>{user.name}</div>
              <div className="meta">{user.email}</div>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 4 }}>Email</h3>
          <p className="meta" style={{ marginBottom: 16 }}>
            Your Google email is used to sign in and can't be changed. Notifications go to your
            primary email.
          </p>

          <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
            Google account email
          </label>
          <input
            type="email"
            value={user.email || ''}
            disabled
            style={{
              width: '100%',
              border: '1px solid var(--hairline)',
              borderRadius: 8,
              padding: '10px 12px',
              fontSize: 14,
              fontFamily: 'inherit',
              background: 'var(--warm)',
              color: 'var(--ink-secondary)',
              marginBottom: 16,
            }}
          />

          <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
            Primary email (for notifications)
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="email"
              placeholder={user.email || 'Enter your preferred email'}
              value={primaryEmail}
              onChange={(e) => setPrimaryEmail(e.target.value)}
              style={{
                flex: 1,
                border: '1px solid var(--hairline)',
                borderRadius: 8,
                padding: '10px 12px',
                fontSize: 14,
                fontFamily: 'inherit',
                background: 'var(--paper)',
              }}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={savePrimaryEmail}
              disabled={emailSaving}
            >
              {emailSaving ? 'Saving…' : emailSaved ? 'Saved' : 'Save'}
            </button>
          </div>
          <p className="meta" style={{ marginTop: 8 }}>
            Leave empty to use your Google email.
          </p>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 4 }}>Sign out</h3>
          <p className="meta" style={{ marginBottom: 16 }}>
            Sign out of your account on this device.
          </p>
          <button className="btn btn-ghost btn-sm" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
